import { CURRENCY, FETCH_STATIC_HOTELS, LOCALE, MARKET } from "../../constants";
import { current } from "@reduxjs/toolkit";
import mainApi from "../../api/rtkMainApi";
import { Proposal, ProposalOffer } from "../../types/proposal";

interface FormattedOffer {
  id: string;
  offerId: string;
  capacity: {
    adult: number;
    room: number;
    child: number;
    childAges: number[];
  };
  airports: string[];
  filters?: {
    luggage?: {
      canAddCib: boolean;
      canAddTrolley: boolean;
    },
    flex?: boolean
  }
}

interface ApiResponse<T> {
  data: T;
}

const formatProposalOffers = (offers: ProposalOffer[]) => {
  return offers.map(offer => ({
    id: offer.offerId,
    offerId: offer.offerId,
    capacity: {
      adult: offer.capacity.adults,
      room: offer.capacity.room,
      child: offer.capacity.children,
      childAges: offer.capacity.childAges || [],
    },
    airports: offer.airports,
    filters: offer.filters
  }))
}

export const proposalsApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    // TODO: getProposals is just a placeholder
    getProposals: build.query<any[], void>({
      query: () => {
        return `${window.apiV6Config.path}/b2b/proposal/`
      },
      transformResponse: (response: ApiResponse<any>) => response.data?.proposals || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((item) => ({ type: 'Proposals' as const, id: item.offer.hfOfferId })),
              { type: 'Proposals' as const, id: 'LIST' },
            ]
          : [{ type: 'Proposals' as const, id: 'LIST' }]
    }),
    getProposalById: build.query<Proposal, { proposalId: Proposal['id']; muid: string }>({
      query: ({ proposalId, muid }) => {
        return `${window.apiV6Config.path}/b2b/proposal/${proposalId}?muid=${muid}&locale=${LOCALE}&b2bcustomerview=${window.location.href.indexOf('b2bcustomerview=true') > -1}`
      },
      transformResponse: (response: Proposal) => response,
      providesTags: (result) =>
        result
          ? [{ type: 'Proposal', id: result.id }]
          : ['Proposal']
    }),
    updateProposal: build.mutation<any, { proposalId: Proposal['id']; muid: string; data: any }>({
      query: ({ proposalId, muid, data }) => ({
        url: `${window.apiV6Config.path}/b2b/proposal/${proposalId}?muid=${muid}&locale=${LOCALE}`,
        method: 'POST',
        body: data,
      }),
      async onQueryStarted({ proposalId, muid }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedProposal } = await queryFulfilled;
          console.log('proposalId', proposalId);
          dispatch(
            proposalsApi.util.updateQueryData('getProposalById', { proposalId, muid }, (data: Proposal) => {
              return {
                ...current(data),
                ...updatedProposal,
              };
            })
          );
        } catch (error) {
          console.error('Failed to update proposal', error);
        }
      },
    }),
    deleteProposal: build.mutation<any, { proposalId: Proposal['id']; muid: string }>({
      query: ({ proposalId, muid }) => ({
        url: `${window.apiV6Config.path}/b2b/proposal/${proposalId}?muid=${muid}&locale=${LOCALE}`,
        method: 'DELETE',
      }),
    }),
    getOffersByIds: build.query<any[], { offers: ProposalOffer[]; proposalId: Proposal['id'] }>({
      query: ({ offers, proposalId }) => {
        const formattedOffers: FormattedOffer[] = formatProposalOffers(offers);

        const userData = JSON.parse(localStorage.getItem('hf_user') || '{}');

        return `api_no_auth/holiday_finder/offers/?data=${JSON.stringify({
          locale: LOCALE,
          market: MARKET,
          currency: CURRENCY,
          hfOffers: formattedOffers,
        })}&muid=${userData?.user?.muid}&proposalId=${proposalId}&t=${Date.now()}`
      },
      transformResponse: (response: ApiResponse<any>) => response.data?.offers || [],
      providesTags: ['ProposalOffers']
    }),
    getStaticOffersByIds: build.query<any[], any>({
      query: (params) => {
        return `${FETCH_STATIC_HOTELS}?data=${JSON.stringify({
          locale: LOCALE,
          marketId: MARKET,
          ...params,
        })}`
      },
      transformResponse: (response: ApiResponse<any>) => response.data?.hotels || [],
      providesTags: ['ProposalHotels']
    }),
    removeOfferFromProposal: build.mutation<any, { proposalId: Proposal['id']; offerId: string; muid: string }>({
      query: ({ proposalId, offerId, muid }) => ({
        url: `${window.apiV6Config.path}/b2b/proposal/${proposalId}/offer?muid=${muid}&offerId=${offerId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ proposalId, offerId, muid }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const state = getState() as any;
          const queries = state.mainApi.queries;
          const cacheKeys = Object.keys(queries);
          const matchingKey = cacheKeys.find((key) => key.startsWith("getOffersByIds"));
          const matchingStaticKey = cacheKeys.find((key) => key.startsWith("getStaticOffersByIds"));
          const matchingProposalKey = cacheKeys.find((key) => key.startsWith("getProposalById"));

          if (!matchingKey || !matchingStaticKey) {
            return;
          }

          const cacheKeyMatch = matchingKey.match(/getOffersByIds\((.*)\)/);
          const cacheKey = cacheKeyMatch ? JSON.parse(cacheKeyMatch[1]) : null;
          const cacheStaticKeyMatch = matchingStaticKey.match(/getStaticOffersByIds\((.*)\)/);
          const cacheStaticKey = cacheStaticKeyMatch ? JSON.parse(cacheStaticKeyMatch[1]) : null;
          const hotelToRemove = cacheKey?.offers?.find((offer: any) => offer.offerId === offerId)?.hotelId;

          if (cacheKey) {
            dispatch(
              proposalsApi.util.updateQueryData('getOffersByIds', cacheKey, (draft: any[]) => {
                const offers = current(draft);
                return offers.filter(({ offer }: any) => offer.hfOfferId !== offerId);
              })
            );

            const updatedOffers = cacheKey.offers.filter((offer: any) => offer.offerId !== offerId);
            dispatch(
              proposalsApi.util.updateQueryData('getOffersByIds', { ...cacheKey, offers: updatedOffers }, (draft: any[]) => {
                const offers = current(draft);
                return offers.filter(({ offer }: any) => offer.hfOfferId !== offerId);
              })
            );
          }
          if (cacheStaticKey) {
            dispatch(
              proposalsApi.util.updateQueryData('getStaticOffersByIds', cacheStaticKey, (draft) => {
                const hotels = current(draft);
                return hotels.filter(({ id }: any) => id !== hotelToRemove);
              })
            );
          }

          // Update proposal cache to remove the offer. It saves us from fetching the proposal again.
          if (matchingProposalKey) {
            dispatch(
              proposalsApi.util.updateQueryData('getProposalById', { proposalId, muid }, (draft: any) => {
                if (draft?.offers) {
                  const currentOffers = current(draft.offers);
                  const filteredOffers = currentOffers.filter((offer: any) => offer.offerId !== offerId);
                  return {
                    ...draft,
                    offers: filteredOffers
                  };
                }
                return draft;
              })
            );
          }
        } catch (error) {
          console.error('Failed to remove offer from proposal', error);
        }
      },
    }),
    updateProposalCommission: build.mutation<any, { proposalId: Proposal['id']; commission: number; muid: string }>({
      query: ({ proposalId, commission, muid }) => ({
        url: `${window.apiV6Config.path}/b2b/proposal/${proposalId}?muid=${muid}&locale=${LOCALE}`,
        method: 'POST',
        body: {
          commission,
        },
      }),
      async onQueryStarted({ proposalId, muid }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate both Proposal and ProposalOffers tags to ensure all related data is refetched
          dispatch(proposalsApi.util.invalidateTags([{ type: 'Proposal', id: proposalId }, 'ProposalOffers']));
        } catch (error) {
          console.error('Failed to update proposal commission', error);
        }
      },
    }),
    updateOfferCommission: build.mutation<any, { proposalId: Proposal['id']; offerId: string; commission: number; muid: string }>({
      query: ({ proposalId, offerId, commission, muid }) => ({
        url: `${window.apiV6Config.path}/b2b/proposal/${proposalId}/commission?muid=${muid}`,
        method: 'POST',
        body: {
          offerId,
          commission,
        },
      }),
      invalidatesTags: () => ['Proposal'],
    }),
  }),
})

export const useGetOffersByIdsQueryState = (offers: ProposalOffer[], proposalId: Proposal['id']) => proposalsApi.endpoints.getOffersByIds.useQueryState({
  offers,
  proposalId,
}, { skip: !offers.length || !proposalId });

export const {
  useGetProposalsQuery,
  useGetProposalByIdQuery,
  useLazyGetProposalByIdQuery,
  useUpdateProposalMutation,
  useDeleteProposalMutation,
  useGetOffersByIdsQuery,
  useLazyGetOffersByIdsQuery,
  useLazyGetStaticOffersByIdsQuery,
  useRemoveOfferFromProposalMutation,
  useUpdateProposalCommissionMutation,
  useUpdateOfferCommissionMutation,
} = proposalsApi;
